import { startTransition } from "react";
import { create } from "zustand";
import { UIActions, UIState } from "@interfaces";
import {
  animationSettingsStorage,
  celebrationSettingsStorage,
} from "@lib/localStorage";

export const useUIStore = create<UIState & UIActions>()((set) => ({
  //#region Default Store State
  activeTab: "active",
  viewMode: "list",
  searchQuery: "",
  selectedBadgeTier: "all",
  celebrationMuted: celebrationSettingsStorage.isMuted(),
  animationReduced: animationSettingsStorage.isReduced(),
  feedbackPrefillMessage: "",
  modals: {
    showLoginDialog: false,
    showSignupDialog: false,
    showFeedback: false,
    showCreateCommunityAuthDialog: false,
    showCommunityPicker: false,
    showBadgesInfo: false,
    showHelpCenter: false,
    showTemplateDialog: false, // Campaign Creation Template
  },
  onboardingProgressByUser: {},
  adminOnboardingGuide: null,
  //#endregion

  //#region Store Actions
  setActiveTab: (tab) => set(() => ({ activeTab: tab })),
  setViewMode: (mode) =>
    set((state) => {
      if (state.viewMode === mode) return state; // avoid redundant updates that can loop
      return { viewMode: mode };
    }),
  setSearchQuery: (query) => set(() => ({ searchQuery: query })),
  setSelectedBadgeTier: (tier) => set(() => ({ selectedBadgeTier: tier })),
  setCelebrationMuted: (muted) => {
    celebrationSettingsStorage.setMuted(muted);
    set(() => ({ celebrationMuted: muted }));
  },
  setAnimationReduced: (reduced) => {
    animationSettingsStorage.setReduced(reduced);
    set(() => ({ animationReduced: reduced }));
  },
  setFeedbackPrefillMessage: (message) =>
    set(() => ({ feedbackPrefillMessage: message })),

  setShowLoginDialog: (isOpen: boolean) =>
    startTransition(() => {
      set((state) => ({
        modals: { ...state.modals, showLoginDialog: isOpen },
      }));
    }),

  setShowSignupDialog: (isOpen: boolean) =>
    startTransition(() => {
      set((state) => ({
        modals: { ...state.modals, showSignupDialog: isOpen },
      }));
    }),

  setShowFeedback: (value: boolean) =>
    set((state) => ({
      modals: {
        ...state.modals,
        showFeedback: value,
      },
    })),
  setShowCreateCommunityAuthDialog: (value) =>
    set((state) => ({
      modals: {
        ...state.modals,
        showCreateCommunityAuthDialog: value,
      },
    })),
  setShowCommunityPicker: (value) =>
    set((state) => ({
      modals: {
        ...state.modals,
        showCommunityPicker: value,
      },
    })),
  setShowBadgesInfo: (value) =>
    set((state) => ({
      modals: {
        ...state.modals,
        showBadgesInfo: value,
      },
    })),
  setShowHelpCenter: (value) =>
    set((state) => ({
      modals: {
        ...state.modals,
        showHelpCenter: value,
      },
    })),

  setShowTemplateDialog: (value) =>
    set((state) => ({
      modals: {
        ...state.modals,
        showTemplateDialog: value,
      },
    })),
  setOnboardingProgress: (userId, progress) =>
    set((state) => ({
      onboardingProgressByUser: {
        ...state.onboardingProgressByUser,
        [userId]: progress,
      },
    })),
  setAdminOnboardingGuide: (guide) =>
    set(() => ({
      adminOnboardingGuide: guide,
    })),

  //#endregion
}));
